/**
 * AIAnalysisProvider - AI分析上下文
 * 
 * 提供统一的AI分析功能：
 * - 技术分析
 * - Provider管理
 * - 结果缓存
 */

import { createContext, useContext, useCallback, useState, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import type { AIAnalysisData, ProviderInfo } from './types';

interface AIAnalysisContextValue {
  selectedProvider: string | undefined;
  setSelectedProvider: (provider: string | undefined) => void;
  availableProviders: ProviderInfo[];
  invalidateAnalysis: (symbol: string) => void;
}

const AIAnalysisContext = createContext<AIAnalysisContextValue | null>(null);

export function AIAnalysisProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [selectedProvider, setSelectedProvider] = useState<string | undefined>(undefined);

  // 获取可用的Provider
  const { data: providersData } = useQuery({
    queryKey: ['ai-providers'],
    queryFn: async () => {
      const { data } = await apiClient.getLLMProviders();
      return data as { success: boolean; providers: string[] };
    },
    staleTime: 1000 * 60 * 10, // 10分钟
  });

  const availableProviders: ProviderInfo[] = (providersData?.providers || []).map((name) => ({
    name,
    displayName: getProviderDisplayName(name),
    isAvailable: true,
    models: [],
  }));

  const invalidateAnalysis = useCallback((symbol: string) => {
    queryClient.invalidateQueries({ queryKey: ['ai-technical-analysis', symbol] });
  }, [queryClient]);

  return (
    <AIAnalysisContext.Provider
      value={{
        selectedProvider,
        setSelectedProvider,
        availableProviders,
        invalidateAnalysis,
      }}
    >
      {children}
    </AIAnalysisContext.Provider>
  );
}

export function useAIAnalysisContext(): AIAnalysisContextValue {
  const context = useContext(AIAnalysisContext);
  if (!context) {
    throw new Error('useAIAnalysisContext must be used within AIAnalysisProvider');
  }
  return context;
}

/**
 * Hook: AI技术分析
 */
export function useAITechnicalAnalysis(
  symbol: string,
  options?: {
    stockName?: string;
    provider?: string;
    enabled?: boolean;
  }
) {
  const context = useContext(AIAnalysisContext);
  const provider = options?.provider || context?.selectedProvider;

  return useQuery({
    queryKey: ['ai-technical-analysis', symbol, options?.stockName, provider],
    queryFn: async () => {
      const { data } = await apiClient.getLLMTechnicalAnalysis(symbol, {
        name: options?.stockName,
        provider,
      });
      return transformAIAnalysisData(data);
    },
    enabled: !!symbol && (options?.enabled !== false),
    staleTime: 1000 * 60 * 5, // 5分钟缓存
    retry: false, // AI分析失败不重试
  });
}

/**
 * Hook: 手动触发AI分析
 */
export function useAIAnalysisMutation() {
  const context = useContext(AIAnalysisContext);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      symbol,
      stockName,
      provider,
    }: {
      symbol: string;
      stockName?: string;
      provider?: string;
    }) => {
      const selectedProvider = provider || context?.selectedProvider;
      const { data } = await apiClient.getLLMTechnicalAnalysis(symbol, {
        name: stockName,
        provider: selectedProvider,
      });
      return transformAIAnalysisData(data);
    },
    onSuccess: (data, variables) => {
      // 更新缓存
      queryClient.setQueryData(
        ['ai-technical-analysis', variables.symbol, variables.stockName, variables.provider],
        data
      );
    },
  });
}

/**
 * Hook: 获取可用Provider列表
 */
export function useAIProviders() {
  return useQuery({
    queryKey: ['ai-providers'],
    queryFn: async () => {
      const { data } = await apiClient.getLLMProviders();
      const providers = (data as { providers: string[] }).providers || [];
      return providers.map((name) => ({
        name,
        displayName: getProviderDisplayName(name),
        isAvailable: true,
      }));
    },
    staleTime: 1000 * 60 * 10,
  });
}

/**
 * 转换API数据为统一格式
 */
function transformAIAnalysisData(data: unknown): AIAnalysisData {
  const d = data as Record<string, unknown>;
  
  return {
    success: Boolean(d.success),
    analysis: d.analysis as AIAnalysisData['analysis'],
    indicators: d.indicators as Record<string, unknown> | undefined,
    supportResistance: d.support_resistance as AIAnalysisData['supportResistance'],
    currentPrice: d.current_price as number | undefined,
    provider: d.provider as string | undefined,
    model: d.model as string | undefined,
    latencyMs: d.latency_ms as number | undefined,
    cached: Boolean(d.cached),
    error: d.error as string | undefined,
  };
}

/**
 * 获取Provider显示名称
 */
function getProviderDisplayName(name: string): string {
  const displayNames: Record<string, string> = {
    deepseek: 'DeepSeek',
    qwen: '通义千问',
    doubao: '豆包',
    claude: 'Claude',
  };
  return displayNames[name.toLowerCase()] || name;
}
